/**
 * Turning a result envelope into the UI work it implies (Issue #12949).
 *
 * Pure, and separated from the component that performs it, because "what
 * should happen to the screen" is the part worth pinning in a test and
 * `router.push` / `element.focus()` are the parts that need a browser.
 *
 * ## Nothing here is taken on trust
 *
 * The envelope is produced by our own server, so in principle every field is
 * ours. `isAppRelativeUrl` exists anyway. The value in `navigation.url` began
 * life as a record id that a browser published into a workspace snapshot, and
 * an audit that has to reason about where a URL came from — through the
 * snapshot, the tool call, the handler and the SSE frame — is an audit that
 * eventually gets it wrong. One check at the point of use costs a line and
 * makes the answer local: an absolute URL, a `javascript:` scheme or a
 * protocol-relative `//host` never reaches `router.push`.
 */

import type { WorkspaceActionOutcome } from './types'

/**
 * Whether `url` is a path inside this app.
 *
 * Rejects protocol-relative `//evil.example` — which `new URL(u, base)` would
 * happily resolve to another origin — and anything carrying a scheme,
 * backslash or control character.
 */
export function isAppRelativeUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false
  if (!url.startsWith('/')) return false
  if (url.startsWith('//')) return false
  if (url.includes('\\')) return false
  // biome-ignore lint/suspicious/noControlCharactersInRegex: bounding input
  return !/[\u0000-\u001f\u007f]/.test(url)
}

/**
 * The chat's reply to a succeeded outcome that made a record (#16109): its
 * message and a markdown link to the record. One function for the live reply
 * (the coordinator) and the persisted one (`note-chat-writes`), so a reload
 * reads exactly what was shown. `null` when there is nothing safe to link.
 */
export function formatWorkspaceChatReply(
  message: string,
  link: { url: string; label: string } | undefined,
): string | null {
  if (!link || !isAppRelativeUrl(link.url)) return null
  // The message carries user-written text (a note title): escaped, so a
  // bracketed title cannot become a link of its own in the reply.
  const text = message.replace(/[[\]]/g, char => `\\${char}`)
  const label = link.label.replace(/[[\]]/g, '')
  // A `(`, `)` or space would end the markdown link early.
  const url = link.url.replace(
    /[()\s]/g,
    char =>
      `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`,
  )
  return `${text} [${label}](${url})`
}

export interface WorkspaceOutcomePlan {
  /** Where to send the user, when the outcome asked for navigation. */
  navigateTo?: string
  /** Whether to re-fetch the current route's server components. */
  refreshRoute: boolean
  /** A record to move focus to once the DOM has caught up. */
  focusEntityId?: string
  /** Ids a surface holding local state should reconcile. */
  changedEntityIds: string[]
  /** What to announce politely to assistive technology. */
  announcement: string
  /** Whether the user is being asked to approve something. */
  needsConfirmation: boolean
  /** What chat replies with, when the outcome made a record (#16109). */
  chatReply?: string
}

/**
 * Decide what the client should do about one outcome.
 *
 * A refusal or a failure plans NO navigation, NO refresh and NO focus move —
 * only an announcement. That asymmetry is the "failure leaves the surface
 * coherent" requirement: nothing changed on the server, so nothing about the
 * screen should move, and the user is told in words rather than by a page
 * that reloaded for no visible reason.
 */
export function planWorkspaceOutcome(
  outcome: WorkspaceActionOutcome,
): WorkspaceOutcomePlan {
  const succeeded = outcome.status === 'succeeded'
  const chatReply = succeeded
    ? formatWorkspaceChatReply(outcome.message, outcome.link)
    : null

  return {
    ...(succeeded && isAppRelativeUrl(outcome.navigation?.url)
      ? { navigateTo: outcome.navigation.url }
      : {}),
    // Navigating already renders the destination; refreshing the page being
    // left is work thrown away, and on some routes a visible flash.
    refreshRoute:
      succeeded && outcome.refresh === 'route' && !outcome.navigation,
    ...(succeeded && outcome.focus?.entityId
      ? { focusEntityId: outcome.focus.entityId }
      : {}),
    changedEntityIds: succeeded
      ? outcome.changedEntities.map(entity => entity.id)
      : [],
    announcement: outcome.message,
    needsConfirmation: outcome.status === 'confirmation_required',
    ...(chatReply ? { chatReply } : {}),
  }
}

/**
 * The attribute a surface puts on a record's element to make it focusable by
 * a capability outcome.
 *
 * An attribute rather than a registry callback because focus is a DOM
 * concern and the element that should receive it is the one the surface
 * rendered — a callback would make every surface reimplement `querySelector`.
 */
export const WORKSPACE_ENTITY_ATTRIBUTE = 'data-workspace-entity-id'

/** The selector for one entity's element. Ids are escaped, never interpolated raw. */
export function workspaceEntitySelector(entityId: string): string {
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(entityId)
      : entityId.replace(/["\\]/g, '\\$&')
  return `[${WORKSPACE_ENTITY_ATTRIBUTE}="${escaped}"]`
}
